/**
 * Presenting a specification: the shapes `GET /admin/specs/**` serves, and the pure parts of reading one.
 *
 * The rendering lives in `features/admin/Specifications.tsx`; what is here is what can be tested without a
 * DOM — the filter and the two number formats, both of which have a wrong answer that looks plausible.
 */

export interface SpecValue { code: string; label: string }

export interface SpecItemDef {
  ref: string;
  itemNumber: number;
  subfieldNumber?: number;
  mnemonic: string;
  description?: string;
  characterType: string;
  minLength: number;
  maxLength: number;
  condition?: string;
  characterSet?: string;
  values?: SpecValue[];
  valuesMandatory?: boolean;
  defaultValue?: string;
}

export interface SpecFieldDef extends Omit<SpecItemDef, 'itemNumber' | 'subfieldNumber'> {
  fieldNumber: number;
  occursMin: number;
  occursMax: number;
  longDescription?: string;
  example?: string;
  items: SpecItemDef[];
}

export interface SpecRecordDef { recordType: number; fields: SpecFieldDef[] }

export interface SpecRecordRequirement { recordType: number; min: number; max: number }
export interface SpecTotDef { tot: string; description?: string; records: SpecRecordRequirement[] }
export interface SpecCategoryDef { name: string; transactions: SpecTotDef[] }

export interface SpecSummary {
  id: string;
  tots: string[];
  files: string[];
  /** The entry point among `files` — the only one an inline `SCRIPT_BEGIN` block can live in. */
  ruleFile: string;
}

/**
 * An unbounded maximum, as anijs writes it — `Integer.MAX_VALUE`, matching the Java the rule reader was
 * ported from. The specification file spells it `x`, e.g. `1.003: T1_CNT N 1-x 1-x`.
 *
 * This has to be recognised rather than rendered: a field that may repeat any number of times otherwise
 * reads as "1–2147483647", which looks like a defect in our software rather than a statement about the rule.
 */
export const UNBOUNDED = 2147483647;

/** `5`, `1–120`, or `1+` when the maximum is unbounded. An en dash, because it is a range and not a minus. */
export function formatRange(min: number, max: number): string {
  if (max === UNBOUNDED) return `${min}+`;
  if (min === max) return String(min);
  return `${min}–${max}`;
}

/**
 * How often a record or field may occur, as a phrase rather than a range.
 *
 * `1` is by far the commonest and says nothing worth the space, so it renders as empty and the caller omits
 * the line. `0–14` means optional up to fourteen; `0+` means optional, any number.
 */
export function formatOccurs(min: number, max: number): string {
  if (min === 1 && max === 1) return '';
  if (min === 0 && max === UNBOUNDED) return 'optional, any number';
  if (min === 0 && max === 1) return 'optional';
  if (min === 0) return `optional, up to ${max}`;
  return `${formatRange(min, max)} times`;
}

/** `M` and `O` are what the specification writes. Anywhere a person reads them, write the words. */
export function conditionLabel(condition: string | undefined): string {
  if (condition === 'M') return 'Mandatory';
  if (condition === 'O') return 'Optional';
  return condition ?? '';
}

const norm = (s: string) => s.toLowerCase();

/**
 * Whether a field answers the search box.
 *
 * Matches the reference (`2.041`), the mnemonic (`T2_RES`), and both descriptions — the three things someone
 * arrives knowing. Items are searched too and a match on one keeps the FIELD: an item is not addressable on
 * its own in this view, so hiding its parent would hide the match.
 *
 * Value codes are deliberately NOT searched. T2_SMT alone carries 851 of them, and a query like `sc` would
 * match a hundred scar codes and return every field that happens to have a big table — the opposite of what
 * a filter is for.
 */
export function fieldMatches(field: SpecFieldDef, query: string): boolean {
  const q = norm(query.trim());
  if (!q) return true;
  // Structural, not `SpecItemDef`: a field is not an item — it has no item number — and both are searched.
  const hay = (f: { ref: string; mnemonic: string; description?: string | undefined }) =>
    [f.ref, f.mnemonic, f.description ?? ''].map(norm);
  const inField = [...hay(field), norm(field.longDescription ?? '')];
  if (inField.some((h) => h.includes(q))) return true;
  return field.items.some((i) => hay(i).some((h) => h.includes(q)));
}

/**
 * The records to show for a query, dropping any left with no matching field.
 *
 * An empty query returns the records unchanged — the same array, not a copy, so an unfiltered tree does not
 * re-render every row on each keystroke elsewhere on the page.
 */
export function filterRecords(records: SpecRecordDef[], query: string): SpecRecordDef[] {
  if (!query.trim()) return records;
  return records
    .map((r) => ({ ...r, fields: r.fields.filter((f) => fieldMatches(f, query)) }))
    .filter((r) => r.fields.length > 0);
}

/** Whether a transaction type answers the picker's search box — its code or its description. */
export function totMatches(tot: SpecTotDef, query: string): boolean {
  const q = norm(query.trim());
  if (!q) return true;
  return norm(tot.tot).includes(q) || norm(tot.description ?? '').includes(q);
}

/** The categories to show for a query, dropping any left with no matching transaction type. */
export function filterCategories(categories: SpecCategoryDef[], query: string): SpecCategoryDef[] {
  if (!query.trim()) return categories;
  return categories
    .map((c) => ({ ...c, transactions: c.transactions.filter((t) => totMatches(t, query)) }))
    .filter((c) => c.transactions.length > 0);
}

/** A script the specification runs — an included file, or a `SCRIPT_BEGIN` block in the rule file itself. */
export interface SpecScriptDef {
  /** The include file it came from. Absent for an inline block. */
  name?: string;
  type: string;
  bytes: number;
  content: string;
}

/** A `.js` file. NOT the same question as "does this specification execute it" — see `fileScriptRole`. */
export const isScriptFile = (path: string) => path.toLowerCase().endsWith('.js');

/** What a file in a specification's directory actually is, once you know what the specification loads. */
export type FileScriptRole =
  /** An included script. This is code, and this specification runs it. */
  | 'loaded-script'
  /** A `.js` sitting in the directory that this specification never loads. Editing it changes nothing. */
  | 'orphan-script'
  /** A rule file that carries a `SCRIPT_BEGIN` block. Named like data, executes like code. */
  | 'inline-script'
  /** Rules and lookup tables. */
  | 'data';

/**
 * Which of those a file is.
 *
 * Extension alone gets the ATF specification backwards in both directions. It includes NO scripts and inlines
 * 96KB into `atf-eft.txt`, while `nistparse.js` and `aft-eft.js` sit unused in the same directory — so the
 * `.js` files are the dead ones and the `.txt` is the executable one. `scripts` is what the specification
 * reports loading, which is the only thing that settles it.
 *
 * `ruleFile` is the entry point named in the registry; it is the only file an inline block can be in.
 */
export function fileScriptRole(
  path: string,
  scripts: SpecScriptDef[],
  ruleFile: string | undefined,
): FileScriptRole {
  if (scripts.some((s) => s.name === path)) return 'loaded-script';
  if (isScriptFile(path)) return 'orphan-script';
  if (path === ruleFile && scripts.some((s) => !s.name)) return 'inline-script';
  return 'data';
}

/** Bytes, for a script listing. Kilobytes past a kilobyte — nobody reads "96438". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  return `${Math.round(bytes / 1024)} KB`;
}
